import { useState, useEffect } from 'react';
import { useTheme } from './theme';
import { db } from './dbClient';
import { useToast } from './Toast';

export default function ManualCaseCreation({ company, team = [], onCaseCreated, onClose }) {
  const C = useTheme();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [formData, setFormData] = useState({
    entity_name: '',
    entity_type: 'customer',
    reason: '',
    tags: [],
    assigned_to: '',
    priority: 'normal',
    risk_level: 'HIGH',
  });

  useEffect(() => {
    if (company?.id) {
      loadScenarios();
    }
  }, [company?.id]);

  async function loadScenarios() {
    try {
      const { data, error } = await db
        .from('scenario_types')
        .select('id, code, name')
        .eq('company_id', company.id)
        .order('name');

      if (error) throw error;
      setScenarios(data || []);
    } catch (err) {
      console.error('Error loading scenarios:', err);
    }
  }

  async function handleSubmit() {
    if (!formData.entity_name.trim()) {
      showToast('Entity name is required', 'error');
      return;
    }

    try {
      setLoading(true);

      // Create case
      const { data: caseData, error: caseError } = await db
        .from('cases')
        .insert([{
          company_id: company.id,
          entity: formData.entity_name,
          type: `Manual: ${formData.entity_type}`,
          risk: formData.risk_level,
          status: 'open',
          assigned_to: formData.assigned_to || null,
          investigation_notes: formData.reason,
          alert_generated_at: new Date().toISOString(),
          detection_source: 'Manual Case Creation',
        }])
        .select();

      if (caseError) throw caseError;

      const newCase = caseData?.[0];
      if (!newCase) throw new Error('Case creation returned no data');

      // Create manual case record
      const { error: manualError } = await db
        .from('manual_cases')
        .insert([{
          company_id: company.id,
          case_id: newCase.id,
          entity_name: formData.entity_name,
          entity_type: formData.entity_type,
          reason: formData.reason,
          tags: formData.tags,
          assigned_to: formData.assigned_to || null,
          priority: formData.priority,
        }]);

      if (manualError) throw manualError;

      // Add scenario tags if selected
      if (formData.tags.length > 0) {
        for (const scenarioId of formData.tags) {
          await db
            .from('case_scenarios')
            .insert([{
              case_id: newCase.id,
              scenario_type_id: scenarioId,
              detected_date: new Date().toISOString(),
              is_primary: formData.tags[0] === scenarioId,
            }]);
        }
      }

      showToast('Case created successfully', 'success');
      onCaseCreated?.(newCase);
      onClose?.();
    } catch (err) {
      showToast('Error creating case', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.75)',
      zIndex: 300,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: '48px',
      overflowY: 'auto'
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        padding: 28,
        width: 560,
        maxWidth: '95vw',
        fontFamily: "'DM Mono',monospace",
        marginBottom: 48
      }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
            Create Case Manually
          </div>
          <div style={{ color: C.textDim, fontSize: 12 }}>
            Create a case for ad-hoc investigation or thematic review
          </div>
        </div>

        <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
          {/* Entity Name */}
          <div>
            <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
              Entity Name *
            </label>
            <input
              type="text"
              value={formData.entity_name}
              onChange={(e) => setFormData({...formData, entity_name: e.target.value})}
              placeholder="e.g., Company Ltd, John Smith"
              style={{
                width: '100%',
                padding: '0.6rem',
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                color: C.text,
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.9rem'
              }}
            />
          </div>

          {/* Entity Type & Risk Level */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Entity Type
              </label>
              <select
                value={formData.entity_type}
                onChange={(e) => setFormData({...formData, entity_type: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              >
                <option value="customer">Customer</option>
                <option value="transaction">Transaction</option>
                <option value="third_party">Third Party</option>
                <option value="counterparty">Counterparty</option>
              </select>
            </div>

            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Risk Level
              </label>
              <select
                value={formData.risk_level}
                onChange={(e) => setFormData({...formData, risk_level: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              >
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
              Reason for Manual Creation
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              placeholder="Why is this case being created?"
              style={{
                width: '100%',
                padding: '0.6rem',
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                color: C.text,
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.9rem',
                minHeight: '80px',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Scenario Tags */}
          {scenarios.length > 0 && (
            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Scenario Tags
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                {scenarios.map(scenario => (
                  <label key={scenario.id} style={{ color: C.text, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.tags.includes(scenario.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({...formData, tags: [...formData.tags, scenario.id]});
                        } else {
                          setFormData({...formData, tags: formData.tags.filter(id => id !== scenario.id)});
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>{scenario.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Assign To */}
          <div>
            <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
              Assign To
            </label>
            <select
              value={formData.assigned_to}
              onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
              style={{
                width: '100%',
                padding: '0.6rem',
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                color: C.text,
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.9rem'
              }}
            >
              <option value="">Unassigned</option>
              {team.map(member => (
                <option key={member.id} value={member.id}>
                  {member.firstName || member.first_name} {member.lastName || member.last_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: '0.6rem 1.2rem',
              background: C.accent,
              color: '#0A0E1A',
              border: 'none',
              borderRadius: 6,
              fontWeight: 700,
              cursor: loading ? 'wait' : 'pointer',
              fontFamily: "'DM Mono', monospace",
              fontSize: '0.85rem',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Creating...' : 'Create Case'}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '0.6rem 1.2rem',
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.textMid,
              borderRadius: 6,
              cursor: 'pointer',
              fontFamily: "'DM Mono', monospace",
              fontSize: '0.85rem'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
